import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Drawer,
  IconButton,
  useTheme,
  useMediaQuery,
  Toolbar,
  AppBar,
  Container,
} from '@mui/material';
import {
  Menu as MenuIcon,
  PlayArrow,
  CalendarToday,
  Assignment,
  AdminPanelSettings,
  Payment,
  Receipt,
  Sms,
  People,
  Settings,
  Help,
} from '@mui/icons-material';
import LanguageSwitcher from './LanguageSwitcher';
import DocsSection from './DocsSection';

// Import translation files
import enDocs from '../../locales/docs/en.json';
import noDocs from '../../locales/docs/no.json';

const DOCS_LANGUAGE_KEY = 'docs_language';
const DRAWER_WIDTH = 280;

// Section icons mapping
const sectionIcons = {
  getting_started: <PlayArrow />,
  bookings: <CalendarToday />,
  job_board: <Assignment />,
  admin_board: <AdminPanelSettings />,
  payments: <Payment />,
  invoices: <Receipt />,
  chat: <Sms />,
  customers: <People />,
  settings: <Settings />,
  troubleshooting: <Help />,
};

/**
 * Main Docs page component with sidebar navigation and language switching.
 * Displays training documentation for admins.
 */
const Docs = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Language state - persisted in localStorage
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem(DOCS_LANGUAGE_KEY) || 'en';
  });

  // Mobile drawer state
  const [mobileOpen, setMobileOpen] = useState(false);

  // Active section for highlighting
  const [activeSection, setActiveSection] = useState('getting_started');

  // Get docs content based on language
  const docs = language === 'no' ? noDocs : enDocs;
  const sections = docs.sections || {};
  const sectionKeys = Object.keys(sections);

  // Persist language choice
  useEffect(() => {
    localStorage.setItem(DOCS_LANGUAGE_KEY, language);
  }, [language]);

  const handleLanguageChange = (newLang) => {
    setLanguage(newLang);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSectionClick = (sectionKey) => {
    setActiveSection(sectionKey);

    // Scroll to section
    const element = document.getElementById(sectionKey);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    // Close mobile drawer
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  // Update active section based on scroll position
  useEffect(() => {
    const handleScroll = () => {
      const scrollContainer = document.getElementById('docs-content');
      if (!scrollContainer) return;

      const scrollTop = scrollContainer.scrollTop;

      for (const key of sectionKeys) {
        const element = document.getElementById(key);
        if (element) {
          const { offsetTop } = element;
          if (scrollTop >= offsetTop - 100) {
            setActiveSection(key);
          }
        }
      }
    };

    const scrollContainer = document.getElementById('docs-content');
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
      return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }
  }, [sectionKeys]);

  // Sidebar content
  const drawerContent = (
    <Box sx={{ overflow: 'auto', mt: 2 }}>
      <Typography
        variant="subtitle2"
        color="text.secondary"
        sx={{ px: 2, mb: 1, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}
      >
        {language === 'no' ? 'Seksjoner' : 'Sections'}
      </Typography>
      <List>
        {sectionKeys.map((key) => (
          <ListItem key={key} disablePadding>
            <ListItemButton
              selected={activeSection === key}
              onClick={() => handleSectionClick(key)}
              sx={{
                '&.Mui-selected': {
                  bgcolor: 'primary.light',
                  color: 'primary.contrastText',
                  '& .MuiListItemIcon-root': {
                    color: 'primary.contrastText',
                  },
                },
                '&.Mui-selected:hover': {
                  bgcolor: 'primary.main',
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                {sectionIcons[key] || <PlayArrow />}
              </ListItemIcon>
              <ListItemText
                primary={sections[key]?.title || key}
                primaryTypographyProps={{
                  variant: 'body2',
                  fontWeight: activeSection === key ? 600 : 400,
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
      {/* Mobile App Bar (shown only on mobile) */}
      {isMobile && (
        <AppBar
          position="fixed"
          sx={{
            top: 64, // Below main app bar
            width: '100%',
            bgcolor: 'background.paper',
            color: 'text.primary',
            boxShadow: 1,
          }}
        >
          <Toolbar sx={{ justifyContent: 'space-between' }}>
            <IconButton
              color="inherit"
              edge="start"
              onClick={handleDrawerToggle}
            >
              <MenuIcon />
            </IconButton>
            <LanguageSwitcher language={language} onLanguageChange={handleLanguageChange} />
          </Toolbar>
        </AppBar>
      )}

      {/* Sidebar Drawer */}
      <Box
        component="nav"
        sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
              top: 128, // Below both app bars
            },
          }}
        >
          {drawerContent}
        </Drawer>

        {/* Desktop drawer */}
        <Paper
          elevation={0}
          sx={{
            display: { xs: 'none', md: 'block' },
            width: DRAWER_WIDTH,
            height: '100%',
            borderRight: '1px solid',
            borderColor: 'divider',
            overflow: 'auto',
          }}
        >
          {drawerContent}
        </Paper>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        id="docs-content"
        sx={{
          flexGrow: 1,
          overflow: 'auto',
          bgcolor: 'background.default',
          mt: isMobile ? 8 : 0, // Account for mobile toolbar
        }}
      >
        <Container maxWidth="lg" sx={{ py: 4 }}>
          {/* Header */}
          <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
                {docs.title}
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                {docs.subtitle}
              </Typography>
            </Box>
            {!isMobile && (
              <LanguageSwitcher language={language} onLanguageChange={handleLanguageChange} />
            )}
          </Box>

          {/* Sections */}
          {sectionKeys.map((key) => (
            <DocsSection key={key} section={sections[key]} sectionKey={key} />
          ))}

          {/* Footer */}
          <Box sx={{ mt: 6, pt: 4, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary" align="center">
              {language === 'no'
                ? 'Sist oppdatert: Mars 2026 | Versjon 1.0'
                : 'Last updated: March 2026 | Version 1.0'
              }
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Docs;
